import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Category, DraftCartLine, JsonOrder, Product, QrCodeDoc } from "@/lib/types";
import type { RestaurantInfo } from "./useRestaurant";
import type { ActiveMenuSettings } from "./useActiveMenuSettings";
import type { OrderHistoryEntry } from "./useQuantityHistory";
import { formatRemaining, getEffectiveKitchenHours, getKitchenStatus } from "@/lib/kitchenHours";
import { preValidate } from "@/lib/limits";
import { isALaCarte, isOrderingSuspended } from "@/lib/restaurantSettings";
import { countBuffetTours } from "@/lib/tourCount";

export type SubmitBlockReason =
  | "offline"
  | "frozen"
  | "can_order_off"
  | "cart_empty"
  | "max_orders"
  | "kitchen_closed"
  | "cooldown"
  | "max_quantity"
  | "max_quantity_per_menu"
  | "limit_failure";

export interface SubmitGate {
  canSubmit: boolean;
  reason: SubmitBlockReason | null;
  /** Short hint rendered under the CTA. */
  message: string;
  /** Button label. */
  cta: string;
  /** Optional human-readable cooldown string ("2 min 13 s") for live display. */
  cooldownRemaining?: string;
  /**
   * Préavis « dernière commande à HH:MM », affiché pendant les
   * DEADLINE_NOTICE_MINUTES qui précèdent la fermeture. Indépendant de
   * `canSubmit` : la commande passe toujours, on prévient seulement que la
   * fenêtre se referme. La borne et la tablette l'affichent déjà (footerNotice
   * / kitchenStatus.showDeadline) ; il manquait sur le canal QR, où le client
   * voyait le bouton fonctionner puis se bloquer sans aucun avertissement.
   */
  deadlineNotice?: string;
}

interface Input {
  online: boolean;
  isFrozen: boolean;
  draftLines: DraftCartLine[];
  customerNb: number;
  productsById: Map<string, Product>;
  categoriesById: Map<string, Category>;
  quantityHistory: Record<string, number>;
  categoryQuantityHistory: Record<string, number>;
  orderHistory: OrderHistoryEntry[];
  firestoreOrders: JsonOrder[];
  lastSentAt: number | null;
  restaurant: RestaurantInfo | null;
  activeMenuSettings: ActiveMenuSettings;
  qrCode: QrCodeDoc | null;
}

/**
 * Source unique de vérité pour la désactivation du bouton "Envoyer en cuisine".
 * Réplique exactement la logique de la tablette dans Cart.js:_deriveSubmitState
 * (lignes 316-360) et _getFreeBlockedMessage (lignes 362-379).
 */
export const useSubmitGate = (input: Input): SubmitGate => {
  const { t, i18n } = useTranslation();
  const CTA = t("submit.cta", "Envoyer ma commande");
  const {
    online,
    isFrozen,
    draftLines,
    customerNb,
    productsById,
    categoriesById,
    quantityHistory,
    categoryQuantityHistory,
    orderHistory,
    firestoreOrders,
    lastSentAt,
    restaurant,
    activeMenuSettings,
    qrCode,
  } = input;

  // Live tick when a cooldown is active so "X min Y s" updates each second.
  const [, setTick] = useState(0);
  // Tic par minute : l'ouverture de la cuisine dépend de l'heure, et le gate est
  // le seul garde-fou (pas de re-vérification à l'envoi ni côté serveur). Sans
  // lui, une page laissée ouverte garderait un `canSubmit` figé après la fermeture.
  const [minuteTick, setMinuteTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setMinuteTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  // Horaires du jour : l'admin écrit dans `settings` (les deux autres champs ne
  // couvrent que des documents historiques). Un jour personnalisé ou fermé
  // remplace les horaires par défaut — même règle que la borne et la tablette.
  const kitchenHours = getEffectiveKitchenHours({
    kitchenHours: restaurant?.settings?.kitchenHours ?? restaurant?.kitchenHours,
    kitchenHoursOverrides: restaurant?.settings?.kitchenHoursOverrides,
    buffetSettings: restaurant?.buffetSettings,
  });
  // `minuteTick` est dans les deps du useMemo : le statut est donc réévalué
  // chaque minute, ce qui fait apparaître le préavis puis le blocage sans
  // qu'aucune interaction ne soit nécessaire.
  const kitchenStatus = getKitchenStatus(kitchenHours);
  const deadlineNotice = kitchenStatus.showDeadline && kitchenStatus.time
    // `kitchen.deadline` est la clé déjà utilisée par la borne et la tablette
    // (« Dernière commande à {{time}} »), présente dans les neuf langues : même
    // formulation d'un canal à l'autre, aucune traduction à ajouter.
    ? t("kitchen.deadline", {
        time: kitchenStatus.time,
        defaultValue: `Dernière commande à ${kitchenStatus.time}`,
      })
    : undefined;

  const waitingMs = (activeMenuSettings.waitingTime || 0) * 60_000;
  const cooldownEndsAt = lastSentAt && waitingMs > 0 ? lastSentAt + waitingMs : 0;
  const cooldownRemainingMs = cooldownEndsAt > 0 ? cooldownEndsAt - Date.now() : 0;
  const cooldownActive = cooldownRemainingMs > 0;

  useEffect(() => {
    if (!cooldownActive) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [cooldownActive, cooldownEndsAt]);

  const gate = useMemo<SubmitGate>(() => {
    if (!online) {
      return { canSubmit: false, reason: "offline", cta: CTA, message: t("submit.offline", "Hors ligne") };
    }
    if (isFrozen) {
      return {
        canSubmit: false,
        reason: "frozen",
        cta: CTA,
        message: t("submit.frozen", "Envoi en cours…"),
      };
    }
    if (isOrderingSuspended(restaurant)) {
      return {
        canSubmit: false,
        reason: "can_order_off",
        cta: CTA,
        message: t("submit.canOrderOff", "Commandes suspendues"),
      };
    }
    if (!draftLines.length) {
      return {
        canSubmit: false,
        reason: "cart_empty",
        cta: CTA,
        message: t("submit.cartEmpty", "Ajoute des plats au panier"),
      };
    }

    // Match the tablet priority: maxOrders > kitchenClosed > cooldown > maxQuantity.
    // Tablet semantics: only orders with at least one free product (and not the
    // initial seeded order) count toward `maxOrders` — see Cart.js:486-490 and
    // countBuffetTours() in tourCount.ts.
    if (
      activeMenuSettings.maxOrders > 0 &&
      countBuffetTours(firestoreOrders) >= activeMenuSettings.maxOrders
    ) {
      return {
        canSubmit: false,
        reason: "max_orders",
        cta: CTA,
        message: t("submit.maxOrders", "Limite de tournées atteinte"),
      };
    }

    if (!kitchenStatus.isOpen) {
      // DIVERGENCE ASSUMÉE avec la borne et la tablette : là-bas, cuisine
      // fermée donne submitMode 'paid-only' — les produits payants (boissons,
      // extras) partent encore, seuls les produits gratuits du buffet sont
      // bloqués. Ici tout est bloqué, parce que la PWA ne construit pas la
      // commande elle-même : elle appelle qrTriggerSubmit, et le serveur
      // (functions/src/lib/executeSubmit.ts) ne connaît ni les horaires
      // cuisine ni la distinction payant/gratuit. Rétablir la parité demande
      // de filtrer les lignes côté serveur, pas de lever ce garde-fou ici —
      // sans quoi c'est le panier ENTIER, produits gratuits compris, qui
      // partirait cuisine fermée.
      //
      // Aucune heure de réouverture dans le message : ambiguë dès qu'il s'agit
      // du lendemain.
      return {
        canSubmit: false,
        reason: "kitchen_closed",
        cta: CTA,
        message: kitchenStatus.time
          ? t("submit.unavailableSince", {
              time: kitchenStatus.time,
              defaultValue: `Envoi de commande indisponible depuis ${kitchenStatus.time}`,
            })
          : t("submit.unavailable", "Envoi de commande indisponible"),
      };
    }

    if (cooldownActive) {
      const remaining = formatRemaining(cooldownRemainingMs);
      return {
        canSubmit: false,
        reason: "cooldown",
        cta: CTA,
        message: t("submit.cooldown", {
          remaining,
          defaultValue: `Patientez ${remaining}`,
        }),
        cooldownRemaining: remaining,
      };
    }

    // Total qty-based caps — mirrors tablet `Restaurant.canAddOneToCart` global
    // branch (Restaurant.js:1044-1054): in forfait mode (!aLaCarte) only free
    // items count; in à-la-carte mode all items count.
    const totalDraftQty = draftLines.reduce((s, l) => s + (l.qty || 0), 0);
    const totalDraftFreeQty = draftLines
      .filter((l) => (l.unitPrice ?? 0) === 0)
      .reduce((s, l) => s + (l.qty || 0), 0);
    const cappedDraftQty = isALaCarte(restaurant) ? totalDraftQty : totalDraftFreeQty;
    if (
      activeMenuSettings.maxQuantity > 0 &&
      cappedDraftQty > activeMenuSettings.maxQuantity * customerNb
    ) {
      return {
        canSubmit: false,
        reason: "max_quantity",
        cta: CTA,
        message: t("submit.maxQuantity", "Quantité totale dépassée"),
      };
    }

    if (activeMenuSettings.maxQuantityPerMenu > 0) {
      // Cumul sur tout le repas des produits GRATUITS seulement — tablette
      // getTotalFreeFromHistory + totalFreeProducts (Cart.js:147-156, 434-444).
      // Compter les payants bloquait trop tôt en formule avec boissons.
      const cumul = orderHistory.reduce((sum, entry) => {
        const ps = entry?.products;
        if (!Array.isArray(ps)) return sum;
        return sum + ps
          .filter((p) => (p?.unitPrice ?? 0) === 0)
          .reduce((ss, p) => ss + (p?.quantity || 1), 0);
      }, 0);
      const maxAllowed =
        activeMenuSettings.maxQuantityPerMenu * activeMenuSettings.menuCount;
      if (cumul + totalDraftFreeQty > maxAllowed) {
        const current = cumul + totalDraftFreeQty;
        return {
          canSubmit: false,
          reason: "max_quantity_per_menu",
          cta: CTA,
          message: t("submit.maxQuantityPerMenu", {
            current,
            max: maxAllowed,
            defaultValue: `Limite menu ${current}/${maxAllowed}`,
          }),
        };
      }
    }

    // Per-product / per-category limits via the existing PWA pre-validator.
    const limit = preValidate({
      customerNb,
      draftLines,
      productsById,
      categoriesById,
      quantityHistory,
      categoryQuantityHistory,
      globalMaxQuantity: activeMenuSettings.maxQuantity,
      aLaCarte: isALaCarte(restaurant),
    });
    if (limit) {
      const labels: Record<typeof limit.code, string> = {
        product_max_quantity: t("submit.limitProductMaxQuantity", "Limite atteinte sur un produit"),
        product_round_quantity: t("submit.limitProductRoundQuantity", "Limite par tournée atteinte"),
        category_max_quantity: t("submit.limitCategoryMaxQuantity", "Limite catégorie atteinte"),
        global_max_quantity: t("submit.limitGlobalMaxQuantity", "Quantité totale dépassée"),
      };
      return {
        canSubmit: false,
        reason: "limit_failure",
        cta: CTA,
        message: labels[limit.code] ?? t("submit.limitGeneric", "Limite atteinte"),
      };
    }

    void qrCode; // reserved for future per-QR overrides
    return { canSubmit: true, reason: null, cta: CTA, message: "" };
  }, [
    online,
    isFrozen,
    restaurant,
    draftLines,
    activeMenuSettings,
    orderHistory,
    firestoreOrders,
    kitchenStatus,
    minuteTick,
    cooldownActive,
    cooldownRemainingMs,
    customerNb,
    productsById,
    categoriesById,
    quantityHistory,
    categoryQuantityHistory,
    qrCode,
    i18n.language,
    CTA,
    t,
  ]);

  // Le préavis est orthogonal au blocage : il doit s'afficher quel que soit
  // l'état du bouton (panier vide, cooldown en cours…), tant que la cuisine
  // est ouverte et proche de sa fermeture.
  return useMemo(
    () => (deadlineNotice ? { ...gate, deadlineNotice } : gate),
    [gate, deadlineNotice],
  );
};
